import type { RefObject } from "react";

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { SegmentOption } from "../../../SegmentControl";
import type { ClientTypeFilterValue } from "../../ClientTypeFilter";

import { BottomSheet } from "../../../BottomSheet";
import { Button } from "../../../Button";
import { FilterButtonGroup } from "../../../FilterButtonGroup";
import { useScrollOnExpand } from "../../../hooks/useScrollOnExpand";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  BadgeIcon,
  CheckIcon,
  ChevronRightIcon,
  FilterIcon,
} from "../../../Icons";
import { MobileDateRangeBottomSheet } from "../../../MobileDateRange/MobileDateRangeBottomSheet";
import { SearchInput } from "../../../SearchInput";
import { SegmentControl } from "../../../SegmentControl";
import { TextButton } from "../../../TextButton";
import { Typo } from "../../../Typo";
import {
  ALL_CLIENT_TYPES,
  includesSharedClients,
} from "../../ClientTypeFilter";
import { useClientTypeFilterTranslate } from "../../ClientTypeFilter/useClientTypeFilterTranslate";

// Stable module-level references for the optional list/selection props below.
// A `= []` default parameter (or an inline `?? []` at a call site) creates a
// *new* array on every render, which -- combined with an effect keyed on that
// prop's reference -- causes an infinite render loop the moment anything else
// re-renders the component. Defaulting to these shared constants instead
// keeps the reference stable across renders when the caller omits the prop.
const EMPTY_OPTIONS: { label: string; value: string }[] = [];
const EMPTY_SELECTION: string[] = [];

type MobileFiltersBottomSheetProps = {
  handleSearchInput: (value: string) => void;
  canViewPII: boolean;
  redactedFields?: string[];
  searchQuery: string;
  rebateAccountOptions: { label: string; value: string }[];
  selectedRebateAccountIDs: string[];
  selectedDateOption?: string;
  showDateFilter?: boolean;
  expandRebateAccounts?: boolean;
  containerClassName?: string;
  /** Forwarded to the search box's own `inputClassName` -- lets callers
   * override its outline treatment (e.g. matching the flatter desktop
   * look) without changing it for every report that shares this component. */
  searchInputClassName?: string;
  onOpenChange?: (isOpen: boolean) => void;
  onApply: (params: {
    dateOption: string;
    dateRange: { from: Date; to: Date };
    rebateAccountIDs: string[];
    rebateProgramIDs: string[];
    clientTypes: ClientTypeFilterValue[];
    subIBIDs: string[];
  }) => void;
  /** Called after Reset clears the local draft. Reset must never call an
   * API itself -- only Apply should. Optional since most callers don't
   * need to react at all; the draft reset already happens internally. */
  onReset?: () => void;
  /** Sheet title. Defaults to "Filters" (existing copy) when omitted. */
  title?: string;
  /** "Dates" section label/hint overrides -- default to the existing generic copy. */
  dateSectionLabel?: string;
  dateSectionHint?: string;
  /** Restrict the rendered date presets to this subset of ids (default: all). */
  dateOptionIds?: string[];
  /** When true, an empty selection reads as "All" instead of "0 selected" --
   * matches reports where an empty filter means "no restriction" rather than
   * the legacy "select all accounts by default" model. */
  emptyMeansAll?: boolean;
  showRebateProgramFilter?: boolean;
  rebateProgramOptions?: { label: string; value: string }[];
  selectedRebateProgramIDs?: string[];
  /** Number of currently-active filters, shown in the trigger button's
   * badge (hidden entirely when 0). Mirrors the desktop Filter modal's
   * `filterCount`, computed by the caller since the formula (e.g. whether a
   * default date range always counts as 1) is report-specific. When
   * omitted, falls back to the existing unconditional dot indicator. */
  filterCount?: number;
  /** Prepend an "All time" preset to the date options list, resolving to
   * `{ from: allTimeStart, to: today }`. Scoped to callers that opt in so
   * other reports' date lists stay unchanged. */
  includeAllTime?: boolean;
  /** "All time" preset start date (required when `includeAllTime` is true).
   * Share the same `Date` instance the report's own date-range codec/provider
   * uses so `getShortcutButtonLabel` collapses to this preset. */
  allTimeStart?: Date;
  /** Translated label for the "All time" preset. */
  allTimeLabel?: string;
  showClientTypeFilter?: boolean;
  selectedClientTypes?: ClientTypeFilterValue[];
  subIBOptions?: { label: string; value: string }[];
  selectedSubIBIDs?: string[];
  subIBsLoadFailed?: boolean;
  onRetrySubIBs?: () => void;
};

type DateOption = {
  id: string;
  label?: string;
  days?: number;
  type?:
    | "today"
    | "yesterday"
    | "days"
    | "month"
    | "lastMonth"
    | "custom"
    | "allTime";
  /** Required when `type === "allTime"` — the fixed start date (`end` is
   * resolved to today at render time, same as the "days"/"month" presets). */
  start?: Date;
};

const formatDateShort = (date: Date, locale?: string) => {
  const day = date.getDate();
  const month = date.toLocaleString(locale, { month: "short" });
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

// Rebate program and rebate account are mutually exclusive on mobile (matches
// the desktop Filter modal's either-or dimension toggle) -- a full-width
// segment control picks the dimension; only the chosen field's collapsible
// list renders below it.
type FilterDimension = "rebateAccount" | "rebateProgram";

// Matches the desktop Filter modal's default: rebate account only becomes
// the active dimension once the user has actually picked one, otherwise
// rebate program is the default.
const deriveInitialDimension = (
  selectedRebateAccountIDs: string[],
): FilterDimension =>
  selectedRebateAccountIDs.length > 0 ? "rebateAccount" : "rebateProgram";

export const MobileFiltersBottomSheet = ({
  handleSearchInput,
  canViewPII,
  redactedFields,
  searchQuery,
  rebateAccountOptions,
  selectedRebateAccountIDs,
  selectedDateOption = "last30days",
  showDateFilter = true,
  expandRebateAccounts = false,
  containerClassName,
  searchInputClassName,
  onOpenChange,
  onApply,
  onReset,
  title,
  dateSectionLabel,
  dateSectionHint,
  dateOptionIds,
  emptyMeansAll = false,
  showRebateProgramFilter = false,
  rebateProgramOptions = EMPTY_OPTIONS,
  selectedRebateProgramIDs = EMPTY_SELECTION,
  filterCount,
  includeAllTime = false,
  allTimeStart,
  allTimeLabel,
  showClientTypeFilter = false,
  selectedClientTypes = ALL_CLIENT_TYPES,
  subIBOptions = EMPTY_OPTIONS,
  selectedSubIBIDs = EMPTY_SELECTION,
  subIBsLoadFailed = false,
  onRetrySubIBs,
}: MobileFiltersBottomSheetProps) => {
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const [isDateRangeSheetOpen, setIsDateRangeSheetOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [isRebateAccountExpanded, setIsRebateAccountExpanded] =
    useState(expandRebateAccounts);
  const [customDateRange, setCustomDateRange] = useState<{
    from: Date;
    to: Date;
  } | null>(null);
  // The custom range from the LAST genuine Filter-level Apply -- distinct
  // from `customDateRange`, which is a live draft that also changes when
  // the Custom date range sub-sheet's own Apply is clicked. Without this,
  // a draft picked in the sub-sheet and abandoned (closed via X, no
  // Filter-level Apply) would still overwrite `customDateRange` and
  // resurface the next time the sheet opens, even though nothing was
  // actually saved.
  const [appliedCustomDateRange, setAppliedCustomDateRange] = useState<{
    from: Date;
    to: Date;
  } | null>(null);

  const { t, i18n } = useTranslation();
  const clientTypeTranslations = useClientTypeFilterTranslate();
  const translatedContent = useMemo(
    () => ({
      today: t("clientsPage_filters_Bottomsheet_today"),
      yesterday: t("clientsPage_filters_Bottomsheet_yesterday"),
      last7days: t("clientsPage_filters_Bottomsheet_last7days"),
      last30days: t("clientsPage_filters_Bottomsheet_last30days"),
      thisMonth: t("clientsPage_filters_Bottomsheet_thisMonth"),
      lastMonth: t("clientsPage_filters_Bottomsheet_lastMonth"),
      filters: {
        searchWithPIIEmail: t(
          "clientsPage_fundingReport_filters_search_with_pii_email",
        ),
        searchWithPIIName: t(
          "clientsPage_fundingReport_filters_search_with_pii_name",
        ),
        search: t("clientsPage_fundingReport_filters_search"),
        searchNoPII: t("clientsPage_fundingReport_filters_search_no_pii"),
      },
      reset: t("clientsPage_filters_Bottomsheet_reset"),
      apply: t("clientsPage_filters_Bottomsheet_apply"),
      dates: t("clientsPage_filters_Bottomsheet_dates"),
      datesDescription: t("clientsPage_filters_Bottomsheet_datesDescription"),
      customDateRange: t("clientsPage_filters_Bottomsheet_customDateRange"),
      rebateAccount: t("clientsPage_filters_Bottomsheet_rebateAccount"),
      rebateProgram: t("clientsPage_tradingReport_filters_rebateProgram"),
      all: t("clientsPage_filters_Bottomsheet_all"),
      selected: t("clientsPage_filters_Bottomsheet_selected"),
      selectAll: t("clientsPage_filters_Bottomsheet_selectAll"),
    }),
    [t],
  );

  const [localSelectedDates, setLocalSelectedDates] =
    useState<string>(selectedDateOption);
  const [localSelectedRebateAccounts, setLocalSelectedRebateAccounts] =
    useState<string[]>(selectedRebateAccountIDs);
  const [localSelectedRebatePrograms, setLocalSelectedRebatePrograms] =
    useState<string[]>(selectedRebateProgramIDs);
  const [isRebateProgramExpanded, setIsRebateProgramExpanded] = useState(false);
  const [localClientTypes, setLocalClientTypes] =
    useState<ClientTypeFilterValue[]>(selectedClientTypes);
  const [localSubIBIDs, setLocalSubIBIDs] =
    useState<string[]>(selectedSubIBIDs);
  const [isSubIBExpanded, setIsSubIBExpanded] = useState(false);
  const [dimension, setDimension] = useState<FilterDimension>(() =>
    deriveInitialDimension(selectedRebateAccountIDs),
  );

  const rebateAccountWrapperRef = useRef<HTMLDivElement>(null);
  const rebateProgramWrapperRef = useRef<HTMLDivElement>(null);
  const subIBWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSearch(searchQuery);
  }, [searchQuery]);

  // Keyed on the joined content, not the array reference -- a caller that
  // passes a fresh-but-equivalent array on every render (e.g. `value ?? []`)
  // must not re-trigger this effect, or it loops forever (setState -> render
  // -> new reference -> effect fires -> setState -> ...).
  const selectedRebateAccountIDsKey = selectedRebateAccountIDs.join(",");
  const selectedRebateProgramIDsKey = selectedRebateProgramIDs.join(",");

  useEffect(() => {
    setLocalSelectedRebateAccounts(selectedRebateAccountIDs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRebateAccountIDsKey]);

  useEffect(() => {
    setLocalSelectedRebatePrograms(selectedRebateProgramIDs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRebateProgramIDsKey]);

  const selectedClientTypesKey = selectedClientTypes.join(",");
  const selectedSubIBIDsKey = selectedSubIBIDs.join(",");

  useEffect(() => {
    setLocalClientTypes(selectedClientTypes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientTypesKey]);

  useEffect(() => {
    setLocalSubIBIDs(selectedSubIBIDs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubIBIDsKey]);

  useScrollOnExpand(isRebateAccountExpanded, rebateAccountWrapperRef);
  useScrollOnExpand(isRebateProgramExpanded, rebateProgramWrapperRef);
  useScrollOnExpand(isSubIBExpanded, subIBWrapperRef);

  const dateOptions: DateOption[] = useMemo(() => {
    const options: DateOption[] = [
      { id: "today", label: translatedContent.today, type: "today" },
      {
        id: "yesterday",
        label: translatedContent.yesterday,
        type: "yesterday",
      },
      {
        id: "last7days",
        label: translatedContent.last7days,
        days: 7,
        type: "days",
      },
      {
        id: "last30days",
        label: translatedContent.last30days,
        days: 30,
        type: "days",
      },
      {
        id: "thisMonth",
        label: translatedContent.thisMonth,
        type: "month",
      },
      {
        id: "lastMonth",
        label: translatedContent.lastMonth,
        type: "lastMonth",
      },
      {
        id: "custom",
        label: translatedContent.customDateRange,
        type: "custom",
      },
    ];
    if (includeAllTime && allTimeStart) {
      options.unshift({
        id: "allTime",
        label: allTimeLabel,
        type: "allTime",
        start: allTimeStart,
      });
    }
    return options;
  }, [
    translatedContent.today,
    translatedContent.yesterday,
    translatedContent.last7days,
    translatedContent.last30days,
    translatedContent.thisMonth,
    translatedContent.lastMonth,
    translatedContent.customDateRange,
    includeAllTime,
    allTimeStart,
    allTimeLabel,
  ]);

  const searchTitle = useMemo(() => {
    if (canViewPII) {
      if (redactedFields?.includes("name")) {
        return translatedContent?.filters?.searchWithPIIEmail;
      }
      if (redactedFields?.includes("email")) {
        return translatedContent?.filters?.searchWithPIIName;
      }
      return translatedContent?.filters?.search;
    }
    return translatedContent?.filters?.searchNoPII;
  }, [canViewPII, redactedFields, translatedContent]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    handleSearchInput?.(value);
  };

  const handleToggleRebateAccount = () => {
    setIsRebateAccountExpanded(!isRebateAccountExpanded);
  };

  const handleOpenBottomSheet = () => {
    if (showDateFilter) {
      setLocalSelectedDates(selectedDateOption ?? "last30days");
    }
    // Discard any custom-range draft left over from a previous visit that
    // was never actually applied at the Filter level.
    setCustomDateRange(appliedCustomDateRange);
    setLocalSelectedRebateAccounts(selectedRebateAccountIDs as string[]);
    setLocalSelectedRebatePrograms(selectedRebateProgramIDs as string[]);
    setDimension(deriveInitialDimension(selectedRebateAccountIDs));
    setLocalClientTypes(selectedClientTypes);
    setLocalSubIBIDs(selectedSubIBIDs);
    setIsBottomSheetOpen(true);
    onOpenChange?.(true);
  };

  const switchDimension = (next: FilterDimension) => {
    if (next === dimension) return;
    if (next === "rebateProgram") {
      setLocalSelectedRebateAccounts([]);
    } else {
      setLocalSelectedRebatePrograms([]);
    }
    setDimension(next);
  };

  const handleCloseBottomSheet = () => {
    setIsBottomSheetOpen(false);
    onOpenChange?.(false);
  };

  // Resolves a Dates radio option id to its concrete {from, to} range.
  const resolveDateRange = (
    optionId: string,
  ): { from: Date; to: Date } | null => {
    const selectedOption = dateOptions.find((opt) => opt.id === optionId);
    if (!selectedOption) return null;

    const today = new Date();
    switch (selectedOption.type) {
      case "custom": {
        if (customDateRange) {
          return { from: customDateRange.from, to: customDateRange.to };
        }
        // Fallback — shouldn't normally happen since picking custom always
        // goes through the date range sheet first
        const start = new Date(today);
        start.setDate(start.getDate() - 30);
        return { from: start, to: today };
      }
      case "today":
        return { from: today, to: today };
      case "yesterday": {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return { from: yesterday, to: yesterday };
      }
      case "days": {
        const start = new Date(today);
        start.setDate(start.getDate() - (selectedOption.days || 30));
        return { from: start, to: today };
      }
      case "month": {
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        return { from: firstDay, to: today };
      }
      case "lastMonth": {
        const firstDayOfLastMonth = new Date(
          today.getFullYear(),
          today.getMonth() - 1,
          1,
        );
        const lastDayOfLastMonth = new Date(
          today.getFullYear(),
          today.getMonth(),
          0,
        );
        return { from: firstDayOfLastMonth, to: lastDayOfLastMonth };
      }
      case "allTime": {
        return { from: selectedOption.start ?? today, to: today };
      }
      default: {
        const start = new Date(today);
        start.setDate(start.getDate() - 30);
        return { from: start, to: today };
      }
    }
  };

  const handleDateSelection = (optionId: string) => {
    if (optionId === "custom") {
      // First time opening the custom range sheet -- seed it with the
      // currently-selected preset's resolved range (e.g. Last 30 days)
      // instead of leaving Start/End blank.
      setCustomDateRange(
        (prev) => prev ?? resolveDateRange(localSelectedDates),
      );
      // Replace the Filters sheet with the Date Range sheet
      setIsBottomSheetOpen(false);
      setIsDateRangeSheetOpen(true);
      return;
    }
    setLocalSelectedDates(optionId);
  };

  const handleDateRangeBack = () => {
    setIsDateRangeSheetOpen(false);
    setIsBottomSheetOpen(true);
  };

  const handleDateRangeClose = () => {
    setIsDateRangeSheetOpen(false);
  };

  const handleDateRangeApply = (range: { from: Date; to: Date }) => {
    setCustomDateRange(range);
    setLocalSelectedDates("custom");
    setIsDateRangeSheetOpen(false);
    setIsBottomSheetOpen(true);
  };

  const handleAccountToggle = (accountId: string) => {
    if (accountId === "all") {
      if (localSelectedRebateAccounts.length === rebateAccountOptions.length) {
        setLocalSelectedRebateAccounts([]);
      } else {
        setLocalSelectedRebateAccounts(
          rebateAccountOptions.map((a) => a.value),
        );
      }
    } else {
      if (localSelectedRebateAccounts.includes(accountId)) {
        setLocalSelectedRebateAccounts(
          localSelectedRebateAccounts.filter((id) => id !== accountId),
        );
      } else {
        setLocalSelectedRebateAccounts([
          ...localSelectedRebateAccounts,
          accountId,
        ]);
      }
    }
  };

  const handleSelectAllAccounts = () => {
    if (localSelectedRebateAccounts.length === rebateAccountOptions.length) {
      setLocalSelectedRebateAccounts([]);
    } else {
      setLocalSelectedRebateAccounts(rebateAccountOptions.map((a) => a.value));
    }
  };

  const handleToggleRebateProgram = () => {
    setIsRebateProgramExpanded((expanded) => !expanded);
  };

  const handleProgramToggle = (programId: string) => {
    setLocalSelectedRebatePrograms((prev) =>
      prev.includes(programId)
        ? prev.filter((id) => id !== programId)
        : [...prev, programId],
    );
  };

  const handleSelectAllPrograms = () => {
    setLocalSelectedRebatePrograms((prev) =>
      prev.length === rebateProgramOptions.length
        ? []
        : rebateProgramOptions.map((p) => p.value),
    );
  };

  const handleReset = () => {
    setCustomDateRange(null);
    setLocalSelectedDates("last30days");
    setLocalSelectedRebateAccounts([]);
    setLocalSelectedRebatePrograms([]);
    setLocalClientTypes(ALL_CLIENT_TYPES);
    setLocalSubIBIDs([]);
    // Matches the desktop Filter modal's Clear -- dimension is left as-is,
    // only the field selections and dates reset. Draft-only: this must not
    // call an API, so any onReset callback is purely a side-effect hook,
    // never a place to commit to the report context. `appliedCustomDateRange`
    // mirrors the LAST genuinely committed range -- Reset must leave it
    // alone, or abandoning this reset via X/Back (no Filter-level Apply)
    // would desync the reopened draft from what's still actually applied.
    onReset?.();
  };

  const appliedClientTypeSelection = {
    clientTypes: localClientTypes,
    subIBIDs: includesSharedClients(localClientTypes) ? localSubIBIDs : [],
  };

  const handleApply = () => {
    if (!showDateFilter) {
      const today = new Date();
      onApply({
        dateOption: "",
        dateRange: { from: today, to: today },
        rebateAccountIDs: localSelectedRebateAccounts,
        rebateProgramIDs: localSelectedRebatePrograms,
        ...appliedClientTypeSelection,
      });
      handleCloseBottomSheet();
      return;
    }

    const range = resolveDateRange(localSelectedDates);
    if (range) {
      onApply({
        dateOption: localSelectedDates,
        dateRange: range,
        rebateAccountIDs: localSelectedRebateAccounts,
        rebateProgramIDs: localSelectedRebatePrograms,
        ...appliedClientTypeSelection,
      });
      // Promote the draft to "genuinely applied" so it's what gets restored
      // (instead of being discarded) the next time the sheet opens.
      if (localSelectedDates === "custom") {
        setAppliedCustomDateRange(customDateRange);
      }
    }

    handleCloseBottomSheet();
  };

  const allAccountsSelected = useMemo(
    () => localSelectedRebateAccounts.length === rebateAccountOptions.length,
    [localSelectedRebateAccounts.length, rebateAccountOptions.length],
  );

  const selectedAccountsLabel = useMemo(() => {
    if (
      allAccountsSelected ||
      (emptyMeansAll && localSelectedRebateAccounts.length === 0)
    ) {
      return translatedContent.all;
    }
    return `${localSelectedRebateAccounts.length} ${translatedContent.selected}`;
  }, [
    allAccountsSelected,
    emptyMeansAll,
    localSelectedRebateAccounts.length,
    translatedContent.all,
    translatedContent.selected,
  ]);

  const allProgramsSelected = useMemo(
    () =>
      rebateProgramOptions.length > 0 &&
      localSelectedRebatePrograms.length === rebateProgramOptions.length,
    [localSelectedRebatePrograms.length, rebateProgramOptions.length],
  );

  const selectedProgramsLabel = useMemo(() => {
    if (
      allProgramsSelected ||
      (emptyMeansAll && localSelectedRebatePrograms.length === 0)
    ) {
      return translatedContent.all;
    }
    return `${localSelectedRebatePrograms.length} ${translatedContent.selected}`;
  }, [
    allProgramsSelected,
    emptyMeansAll,
    localSelectedRebatePrograms.length,
    translatedContent.all,
    translatedContent.selected,
  ]);

  // Rebate program on the left, rebate account on the right -- matches the
  // desktop Filter modal's dimension toggle order.
  const dimensionOptions: SegmentOption<FilterDimension>[] = [
    { label: translatedContent.rebateProgram, value: "rebateProgram" },
    { label: translatedContent.rebateAccount, value: "rebateAccount" },
  ];

  // Shared collapsible "label + All/N selected trigger -> checkbox list"
  // renderer for both the Rebate account and Rebate program fields -- they
  // differ only in which options/selection/handlers they bind to.
  const renderCollapsibleField = ({
    label,
    options,
    selected,
    onToggle,
    onSelectAll,
    allSelected,
    selectedLabel,
    isExpanded,
    onToggleExpand,
    wrapperRef,
  }: {
    label: string;
    options: { label: string; value: string }[];
    selected: string[];
    onToggle: (value: string) => void;
    onSelectAll: () => void;
    allSelected: boolean;
    selectedLabel: string;
    isExpanded: boolean;
    onToggleExpand: () => void;
    wrapperRef: RefObject<HTMLDivElement | null>;
  }) => (
    <>
      <button
        type="button"
        className="my-2 flex w-full items-center justify-between"
        onClick={onToggleExpand}
        aria-expanded={isExpanded}
      >
        <Typo type="labelSM" textColor="text-text-primary">
          {label}
        </Typo>
        <span className="flex items-center gap-1">
          <Typo type="bodySM" textColor="text-text-selected">
            {selectedLabel}
          </Typo>
          {isExpanded ? (
            <ArrowUpIcon className="h-2 w-2" />
          ) : (
            <ArrowDownIcon className="h-2 w-2" />
          )}
        </span>
      </button>

      <div
        ref={wrapperRef}
        className={`grid transition-[grid-template-rows] duration-300 ease-out ${
          isExpanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className={`overflow-hidden ${isExpanded ? "mt-2" : "mt-0"}`}>
          <div className="space-y-0">
            <label className="block">
              <div className="flex items-center gap-2">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={onSelectAll}
                    className="peer sr-only"
                  />
                  <div className="flex h-6 w-6 items-center justify-center rounded border border-stroke-primary bg-surface-primary peer-checked:bg-surface-inverse-primary">
                    {allSelected && (
                      <CheckIcon className="text-text-inverse-primary" />
                    )}
                  </div>
                </div>
                <Typo type="bodySM">{translatedContent.selectAll}</Typo>
              </div>
            </label>

            {options.map((option) => (
              <label key={option.value} className="block pt-3">
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={selected.includes(option.value)}
                      onChange={() => onToggle(option.value)}
                      className="peer sr-only"
                    />
                    <div className="flex h-6 w-6 items-center justify-center rounded border border-stroke-primary bg-surface-primary peer-checked:bg-surface-inverse-primary">
                      {selected.includes(option.value) && (
                        <CheckIcon className="text-text-inverse-primary" />
                      )}
                    </div>
                  </div>
                  <Typo type="bodySM">{option.label}</Typo>
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>
    </>
  );

  const rebateAccountField = renderCollapsibleField({
    label: translatedContent.rebateAccount,
    options: rebateAccountOptions,
    selected: localSelectedRebateAccounts,
    onToggle: handleAccountToggle,
    onSelectAll: handleSelectAllAccounts,
    allSelected: allAccountsSelected,
    selectedLabel: selectedAccountsLabel,
    isExpanded: isRebateAccountExpanded,
    onToggleExpand: handleToggleRebateAccount,
    wrapperRef: rebateAccountWrapperRef,
  });

  const rebateProgramField = renderCollapsibleField({
    label: translatedContent.rebateProgram,
    options: rebateProgramOptions,
    selected: localSelectedRebatePrograms,
    onToggle: handleProgramToggle,
    onSelectAll: handleSelectAllPrograms,
    allSelected: allProgramsSelected,
    selectedLabel: selectedProgramsLabel,
    isExpanded: isRebateProgramExpanded,
    onToggleExpand: handleToggleRebateProgram,
    wrapperRef: rebateProgramWrapperRef,
  });

  const subIBField = renderCollapsibleField({
    label: clientTypeTranslations.subIBLabel,
    options: subIBOptions,
    selected: localSubIBIDs,
    onToggle: (subIBID) =>
      setLocalSubIBIDs((prev) =>
        prev.includes(subIBID)
          ? prev.filter((id) => id !== subIBID)
          : [...prev, subIBID],
      ),
    onSelectAll: () =>
      setLocalSubIBIDs((prev) =>
        prev.length === subIBOptions.length
          ? []
          : subIBOptions.map((subIB) => subIB.value),
      ),
    allSelected:
      subIBOptions.length > 0 && localSubIBIDs.length === subIBOptions.length,
    selectedLabel:
      localSubIBIDs.length === 0 || localSubIBIDs.length === subIBOptions.length
        ? translatedContent.all
        : `${localSubIBIDs.length} ${translatedContent.selected}`,
    isExpanded: isSubIBExpanded,
    onToggleExpand: () => setIsSubIBExpanded((expanded) => !expanded),
    wrapperRef: subIBWrapperRef,
  });

  return (
    <div
      data-testid="mobile-filters-bottom-sheet"
      className={containerClassName ?? "mt-10 flex flex-row items-center gap-2"}
    >
      <div className="flex-grow">
        <SearchInput
          value={search}
          onChange={handleSearchChange}
          placeholder={searchTitle}
          inputClassName={searchInputClassName}
          className="h-10"
        />
      </div>

      <div className="relative">
        <button
          onClick={handleOpenBottomSheet}
          className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-600 transition-colors hover:bg-blue-700"
          aria-label="Open filters"
        >
          <FilterIcon className="h-6 w-6 text-text-inverse-primary" />
        </button>
        {filterCount === undefined ? (
          <div className="absolute -right-0.5 -top-0.5">
            <BadgeIcon className="h-2 w-2" />
          </div>
        ) : (
          filterCount > 0 && (
            <div className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-surface-inverse-primary px-1">
              <Typo
                element="span"
                type="label2XS"
                textColor="text-text-inverse-primary"
              >
                {filterCount}
              </Typo>
            </div>
          )
        )}
      </div>

      <BottomSheet
        isOpen={isBottomSheetOpen}
        onClose={handleCloseBottomSheet}
        title={title ?? "Filters"}
        footer={
          <div className="flex gap-3">
            <Button
              variant="outline"
              size="md"
              className="flex-1"
              onClick={handleReset}
            >
              {translatedContent.reset}
            </Button>
            <Button
              variant="brand"
              size="md"
              className="flex-1"
              onClick={handleApply}
            >
              {translatedContent.apply}
            </Button>
          </div>
        }
      >
        {showDateFilter && (
          <div className="mb-4">
            <div className="mb-1">
              <Typo type="labelSM" textColor="text-text-primary">
                {dateSectionLabel ?? translatedContent.dates}
              </Typo>
            </div>
            <div className="mb-3">
              <Typo type="bodyXS" textColor="text-text-tertiary">
                {dateSectionHint ?? translatedContent.datesDescription}
              </Typo>
            </div>

            <div className="space-y-3">
              {(dateOptionIds
                ? dateOptions.filter((option) =>
                    dateOptionIds.includes(option.id),
                  )
                : dateOptions
              ).map((option) => {
                const isCustom = option.type === "custom";
                const customSubtitle =
                  isCustom && customDateRange
                    ? `${formatDateShort(customDateRange.from, i18n.language)} – ${formatDateShort(customDateRange.to, i18n.language)}`
                    : null;

                return (
                  <label
                    key={option.id}
                    className="flex cursor-pointer items-center gap-3"
                    onClick={
                      isCustom ? () => handleDateSelection("custom") : undefined
                    }
                  >
                    <div className="relative">
                      <input
                        type="radio"
                        name="dateRange"
                        value={option.id}
                        checked={localSelectedDates === option.id}
                        onChange={() => handleDateSelection(option.id)}
                        className="peer sr-only"
                      />
                      <div className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-stroke-primary bg-surface-primary peer-checked:border-surface-inverse-primary peer-checked:bg-surface-inverse-primary">
                        {localSelectedDates === option.id && (
                          <div className="h-3 w-3 rounded-full bg-surface-primary" />
                        )}
                      </div>
                    </div>
                    <div className="flex flex-1 flex-col gap-0.5">
                      <Typo type="bodySM" textColor="text-text-primary">
                        {option.label}
                      </Typo>
                      {customSubtitle && (
                        <Typo type="bodyXS" textColor="text-text-tertiary">
                          {customSubtitle}
                        </Typo>
                      )}
                    </div>
                    {isCustom && (
                      <ChevronRightIcon
                        className="h-4 w-4 text-icon-primary"
                        aria-hidden="true"
                      />
                    )}
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {showClientTypeFilter && (
          <div
            className={`flex flex-col gap-4 ${
              showDateFilter ? "border-t border-stroke-primary pt-4" : ""
            }`}
          >
            <FilterButtonGroup<ClientTypeFilterValue>
              label={clientTypeTranslations.clientTypeLabel}
              layout="inline"
              options={[
                {
                  label: clientTypeTranslations.directClients,
                  value: "direct",
                },
                {
                  label: clientTypeTranslations.sharedWithSubIB,
                  value: "indirect",
                },
              ]}
              values={localClientTypes}
              onChange={setLocalClientTypes}
              data-testid="clients_filter_sheet_client_type"
            />
            {includesSharedClients(localClientTypes) && (
              <div
                className="border-t border-stroke-primary pt-4"
                data-testid="clients_filter_sheet_sub_ib_select"
              >
                {subIBsLoadFailed ? (
                  <>
                    <Typo type="labelSM" textColor="text-text-primary">
                      {clientTypeTranslations.subIBLabel}
                    </Typo>
                    <div className="mt-2 flex items-center gap-2">
                      <div role="alert" className="flex-1">
                        <Typo
                          element="p"
                          type="bodySM"
                          textColor="text-text-error"
                        >
                          {clientTypeTranslations.subIBLoadError}
                        </Typo>
                      </div>
                      {onRetrySubIBs && (
                        <TextButton
                          size="xs"
                          underlined
                          onClick={onRetrySubIBs}
                          data-testid="clients_filter_sheet_sub_ib_retry_button"
                        >
                          {clientTypeTranslations.retry}
                        </TextButton>
                      )}
                    </div>
                  </>
                ) : (
                  subIBField
                )}
              </div>
            )}
          </div>
        )}
        <div
          className={
            showClientTypeFilter
              ? "mt-4 border-t border-stroke-primary pt-4"
              : showDateFilter
                ? "border-t border-stroke-primary pt-4"
                : ""
          }
        >
          {showRebateProgramFilter ? (
            <>
              <div className="mb-4">
                <SegmentControl
                  fullWidth
                  size="sm"
                  aria-label="Filter by rebate dimension"
                  options={dimensionOptions}
                  value={dimension}
                  onChange={switchDimension}
                />
              </div>
              {dimension === "rebateAccount"
                ? rebateAccountField
                : rebateProgramField}
            </>
          ) : expandRebateAccounts ? (
            <>
              <div className="mb-2 flex items-center justify-between">
                <Typo type="labelSM" textColor="text-text-primary">
                  {translatedContent.rebateAccount}
                </Typo>
                <Typo type="bodySM" textColor="text-text-selected">
                  {selectedAccountsLabel}
                </Typo>
              </div>
              <div className="mt-4 space-y-0">
                <label className="block">
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={allAccountsSelected}
                        onChange={handleSelectAllAccounts}
                        className="peer sr-only"
                      />
                      <div className="flex h-6 w-6 items-center justify-center rounded border border-stroke-primary bg-surface-primary peer-checked:bg-surface-inverse-primary">
                        {allAccountsSelected && (
                          <CheckIcon className="text-text-inverse-primary" />
                        )}
                      </div>
                    </div>
                    <Typo type="bodySM">{translatedContent.selectAll}</Typo>
                  </div>
                </label>

                {rebateAccountOptions.map((account) => (
                  <label key={account.value} className="block pt-3">
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <input
                          type="checkbox"
                          checked={localSelectedRebateAccounts.includes(
                            account.value,
                          )}
                          onChange={() => handleAccountToggle(account.value)}
                          className="peer sr-only"
                        />
                        <div className="flex h-6 w-6 items-center justify-center rounded border border-stroke-primary bg-surface-primary peer-checked:bg-surface-inverse-primary">
                          {localSelectedRebateAccounts.includes(
                            account.value,
                          ) && (
                            <CheckIcon className="text-text-inverse-primary" />
                          )}
                        </div>
                      </div>
                      <Typo type="bodySM">{account.label}</Typo>
                    </div>
                  </label>
                ))}
              </div>
            </>
          ) : (
            rebateAccountField
          )}
        </div>
      </BottomSheet>

      <MobileDateRangeBottomSheet
        isOpen={isDateRangeSheetOpen}
        onClose={handleDateRangeClose}
        onBack={handleDateRangeBack}
        onApply={handleDateRangeApply}
        defaultRange={resolveDateRange("last30days")}
        initialRange={customDateRange}
      />
    </div>
  );
};
